import type { DailyGoalHistoryDay } from "@consistency-circle/shared";

function completionTone(day: DailyGoalHistoryDay): string {
  if (day.totalCount === 0) return "bg-muted-foreground/10";
  if (day.completionPercent >= 80) return "bg-primary";
  if (day.completionPercent >= 40) return "bg-primary/55";
  if (day.completionPercent > 0) return "bg-primary/25";
  return "bg-muted-foreground/15";
}

export function TrendChart({
  days,
  moodByDate,
}: {
  readonly days: DailyGoalHistoryDay[];
  readonly moodByDate: ReadonlyMap<string, number>;
}) {
  const chartHeight = 88;
  const compact = days.length > 10;

  const dayLabel = (localDate: string, index: number): string => {
    const date = new Date(`${localDate}T12:00:00Z`);
    if (!compact) {
      return new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "UTC" }).format(date);
    }
    // Monthly view: only label every 5th column to keep it readable.
    if (index % 5 !== 0) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Goals & mood, day by day
        </p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-[2px] bg-primary" />
            goals done
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            mood
          </span>
        </div>
      </div>

      <div className="flex items-end gap-[3px]">
        {days.map((day) => {
          const mood = moodByDate.get(day.localDate);
          const barHeight =
            day.totalCount === 0
              ? 3
              : Math.max(3, Math.round((day.completionPercent / 100) * chartHeight));

          return (
            <div
              key={day.localDate}
              className="relative flex-1"
              style={{ height: chartHeight }}
              title={`${day.localDate}: ${
                day.totalCount === 0 ? "nothing tracked" : `${day.completionPercent}% of goals`
              }${mood !== undefined ? ` · mood ${mood}/5` : ""}`}
            >
              <div
                className={`absolute inset-x-0 bottom-0 rounded-sm transition-all duration-300 ${completionTone(day)}`}
                style={{ height: barHeight }}
              />
              {mood !== undefined && (
                <div
                  className={`absolute left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full bg-amber-500 ring-2 ring-card ${
                    compact ? "h-1.5 w-1.5" : "h-2.5 w-2.5"
                  }`}
                  style={{ bottom: `${(mood / 5) * 100}%` }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-[3px]">
        {days.map((day, index) => (
          <span
            key={day.localDate}
            className="flex-1 overflow-visible whitespace-nowrap text-center text-[10px] text-muted-foreground"
          >
            {dayLabel(day.localDate, index)}
          </span>
        ))}
      </div>
    </div>
  );
}
